import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { authService } from '../services/api';
import LanguageSelector from '../components/LanguageSelector';
import PhoneNumberInput from '../components/PhoneNumberInput';
import { jwtDecode } from 'jwt-decode';

const Login = () => {
  // 傳統登入狀態
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [clid, setClid] = useState('');

  // 手機號碼登入狀態
  const [loginMode, setLoginMode] = useState('traditional'); // 'traditional' 或 'phone'
  const [countryCode, setCountryCode] = useState('+852');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullPhoneNumber, setFullPhoneNumber] = useState('+852');
  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [step, setStep] = useState(1); // 1: 輸入手機號, 2: 選擇診所, 3: 輸入驗證碼

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const { login, error, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/home';

  useEffect(() => {
    // If user is already authenticated, redirect to the originally requested page or home
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }

    if (localStorage.getItem('clid')) {
      setClid(localStorage.getItem('clid'));
    }
  }, [isAuthenticated, navigate, from]);

  // 傳統登入處理
  const handleTraditionalSubmit = async (e) => {
    e.preventDefault();

    if (!username || !password) {
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    const success = await login(username, password, clid);
    setIsSubmitting(false);

    if (success) {
      navigate(from, { replace: true });
    }
  };

    // 更新完整手機號碼
  const updateFullPhoneNumber = (country, phone) => {
    const full = country + phone;
    setFullPhoneNumber(full);
  };

  // 處理國家代碼變更
  const handleCountryCodeChange = (newCountryCode) => {
    setCountryCode(newCountryCode);
    updateFullPhoneNumber(newCountryCode, phoneNumber);
  };

  // 處理手機號碼變更
  const handlePhoneNumberChange = (newPhoneNumber) => {
    setPhoneNumber(newPhoneNumber);
    updateFullPhoneNumber(countryCode, newPhoneNumber);
  };

  // 手機號碼登入 - 第一步：查詢診所
  const handlePhoneSubmit = async (e) => {
    e.preventDefault();

    if (!phoneNumber.trim()) {
      setLocalError(t('error.phoneRequired'));
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      const response = await authService.phoneLogin(fullPhoneNumber);
      setClinics(response.clinics);
      setStep(2);
    } catch (error) {
      setLocalError(error.response?.data?.detail || t('error.clinicsNotFound'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // 手機號碼登入 - 第二步：發送驗證碼
  const handleSendVerification = async () => {
    if (!selectedClinic) {
      setLocalError(t('error.clinicRequired'));
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      const response = await authService.sendVerification(fullPhoneNumber, selectedClinic);
      setVerificationId(response.verification_id);
      setStep(3);
    } catch (error) {
      setLocalError(error.response?.data?.detail || t('error.sendCodeFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Extract clinic ID from token
  const extractClid = (token) => {
    if (!token) return "CLID0000";

    try {
      const decoded = jwtDecode(token);
      return decoded.clid || "CLID0000";
    } catch (error) {
      return "CLID0000";
    }
  };

  // 手機號碼登入 - 第三步：驗證並登入
  const handleVerifyCode = async (e) => {
    e.preventDefault();

    if (!verificationCode.trim()) {
      setLocalError(t('error.codeRequired'));
      return;
    }

    setIsSubmitting(true);
    setLocalError('');

    try {
      const response = await authService.verifyCode(fullPhoneNumber, selectedClinic, verificationCode);
      // 手動設置token和clid到localStorage並導航
      localStorage.setItem('token', response.access_token);

      // Extract and save clinic ID from JWT token
      const decodedClid = extractClid(response.access_token);
      localStorage.setItem('clid', decodedClid);

      window.location.href = from; // 強制刷新頁面以更新認證狀態
    } catch (error) {
      setLocalError(error.response?.data?.detail || t('error.codeInvalid'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // 重置手機登入狀態
  const resetPhoneLogin = () => {
    setStep(1);
    setCountryCode('+852');
    setPhoneNumber('');
    setFullPhoneNumber('');
    setClinics([]);
    setSelectedClinic('');
    setVerificationCode('');
    setVerificationId('');
    setLocalError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      {/* 語言選擇器 - 右上角 */}
      <div className="absolute top-4 right-4">
        <LanguageSelector />
      </div>

      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('login.title')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('login.subtitle')}
          </p>
        </div>

        {/* 登入模式切換 */}
        <div className="flex rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => {
              setLoginMode('traditional');
              resetPhoneLogin();
              setLocalError('');
            }}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              loginMode === 'traditional'
                ? 'bg-white text-[#075e54] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
                      >
              {t('login.traditional')}
            </button>
            <button
              type="button"
              onClick={() => {
                setLoginMode('phone');
                setLocalError('');
              }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                loginMode === 'phone'
                  ? 'bg-white text-[#075e54] shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('login.phone')}
            </button>
        </div>

        {/* 錯誤訊息 */}
        {(error || localError) && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-red-700">{error || localError}</p>
          </div>
        )}

        {location.state?.message && (
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-4">
            <p className="text-green-700">{location.state.message}</p>
          </div>
        )}

        {/* 傳統登入表單 */}
        {loginMode === 'traditional' && (
          <>
            <p className="mt-4 text-center text-sm text-gray-600">
              {t('login.notRegisteredHint')}
              <button
                type="button"
                onClick={() => {
                  setLoginMode('phone');
                  setLocalError('');
                }}
                className="font-medium text-[#075e54] hover:text-[#064e45] underline focus:outline-none focus:ring-2 focus:ring-[#075e54] focus:ring-offset-1 rounded"
              >
                {t('login.loginHere')}
              </button>
            </p>
            <form className="mt-8 space-y-6" onSubmit={handleTraditionalSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">{t('login.usernameOrEmail')}</label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] focus:z-10 sm:text-sm"
                  placeholder={t('login.usernameOrEmail')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">{t('login.password')}</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] focus:z-10 sm:text-sm"
                  placeholder={t('login.password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="clinicId" className="sr-only">{t('login.clinicId')}</label>
                <input
                  id="clinicId"
                  name="clinicId"
                  type="text"
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] focus:z-10 sm:text-sm"
                  placeholder={t('login.clinicIdPlaceholder')}
                  value={clid}
                  onChange={(e) => setClid(e.target.value)}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                    {t('login.signingIn')}
                  </>
                ) : t('login.signIn')}
              </button>
            </div>
          </form>
          </>
        )}

        {/* 手機號碼登入表單 */}
        {loginMode === 'phone' && (
          <div className="mt-8 space-y-6">
            {/* 第一步：輸入手機號碼 */}
            {step === 1 && (
              <form onSubmit={handlePhoneSubmit}>
                <div className="space-y-4">
                  <PhoneNumberInput
                    countryCode={countryCode}
                    phoneNumber={phoneNumber}
                    onCountryCodeChange={handleCountryCodeChange}
                    onPhoneNumberChange={handlePhoneNumberChange}
                    error={localError && !phoneNumber.trim() ? localError : ''}
                    required={true}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50"
                  >
                    {isSubmitting ? t('phoneLogin.querying') : t('phoneLogin.queryClinics')}
                  </button>
                </div>
              </form>
            )}

            {/* 第二步：選擇診所 */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('phoneLogin.selectClinic')}
                  </label>
                  <div className="space-y-2">
                    {clinics.map((clinic) => (
                      <label key={clinic.id} className="flex items-center p-3 border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer">
                        <input
                          type="radio"
                          name="clinic"
                          value={clinic.id}
                          checked={selectedClinic === clinic.id}
                          onChange={(e) => setSelectedClinic(e.target.value)}
                          className="h-4 w-4 text-[#075e54] focus:ring-[#075e54] border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
                          {clinic.description && (
                            <div className="text-sm text-gray-500">{clinic.description}</div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={resetPhoneLogin}
                    className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54]"
                  >
                    {t('phoneLogin.back')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendVerification}
                    disabled={isSubmitting || !selectedClinic}
                    className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50"
                  >
                    {isSubmitting ? t('phoneLogin.sending') : t('phoneLogin.sendCode')}
                  </button>
                </div>
              </div>
            )}

            {/* 第三步：輸入驗證碼 */}
            {step === 3 && (
              <form onSubmit={handleVerifyCode}>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="verificationCode" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('phoneLogin.verificationCode')}
                    </label>
                    <p className="text-sm text-gray-600 mb-2">
                      {t('phoneLogin.codeSentTo')} {fullPhoneNumber}
                    </p>
                    <input
                      id="verificationCode"
                      name="verificationCode"
                      type="text"
                      required
                      maxLength="6"
                      className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#075e54] focus:border-[#075e54] focus:z-10 sm:text-sm text-center text-lg tracking-widest"
                      placeholder={t('phoneLogin.enterCode')}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54]"
                    >
                      {t('phoneLogin.back')}
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || verificationCode.length !== 6}
                      className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#075e54] hover:bg-[#064e45] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#075e54] disabled:opacity-50"
                    >
                      {isSubmitting ? t('phoneLogin.verifying') : t('phoneLogin.verifyAndLogin')}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
